import { useMemo } from "react"
import { logWarning } from "@/v1-ui/utils/utils.log"
import { useNotifications } from "@/v1-ui/Notification"
import AppContextErrorNotification
  from "@/v1-console/app/context/AppContextErrorNotification"
import useAppContextQuery from "@/v1-console/app/context/app.context.query"
import { registerMapServerTokens }
  from "@/v1-console/map/auth/map.auth.utils"
import { identify } from "@/v1-console/analytics"
import { setUser } from "@/v1-console/sentry"
import config from "@/v1-console/config"
import AppContextProvider, {
  type AppContextProviderProps,
  APP_CTX_LOADED_EVENT,
  DEFAULT_APP_CTX,
  EVENTS
} from "@/v1-console/app/context/AppContextProvider"

const POLL_SKEW = 300_000 // ms - refresh 5 minutes earlier than exp

export type AppContextLoaderProps = Omit<AppContextProviderProps, "value">

function AppContextLoader(props: AppContextLoaderProps) {
  const { addNotification } = useNotifications()

  const { data, refetch, startPolling } = useAppContextQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      layerTokensInput: {
        referer: window.location.origin
      }
    },
    onCompleted(d) {
      if(config.isStaging && !d.getCurrentUser.IsStaff) {
        const { pathname, search, hash } = window.location
        window.location.href = `https://console.placepoint.no${pathname}${search}${hash}`
        return
      }

      registerMapServerTokens(d.getServiceTokens.Tokens)

      const pi = new Date(d.getServiceTokens.ExpiresAt).getTime() - Date.now()
      startPolling(Math.max(10000, pi - POLL_SKEW)) // at least 10s ensures first req has enough time

      setUser(d.getCurrentUser)
      identify(d.getCurrentUser)

      // @note: could add context as event payload if needed
      EVENTS.dispatchEvent(new CustomEvent(APP_CTX_LOADED_EVENT))
    },
    onError(e) {
      const dismiss = addNotification({
        timeout: false,
        intent: "danger",
        title: "En feil oppstod med autentiseringen",
        iconSrc: "exclamation",
        content: (
          <AppContextErrorNotification
            error={e}
            onRetry={() => {
              refetch()
              dismiss()
            }}
            onDismiss={() => {
              dismiss()
            }}
          />
        )
      })
    }
  })

  const context = useMemo(() => {
    if(!data) return DEFAULT_APP_CTX
    const layerServerToken = data
      .getServiceTokens
      .Tokens
      .find((t) => t.ServerUrl.includes("placepoint.no"))
      ?.Token

    if(!layerServerToken) {
      logWarning("Unable to find the token for placepoint layer server")
    }

    return {
      currentUser: data.getCurrentUser,
      events: DEFAULT_APP_CTX.events,
      layerServerToken
    }
  }, [
    data
  ])

  return (
    <AppContextProvider
      value={context}
      {...props}
    />
  )
}

export default AppContextLoader
